import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../api';
import { useProctorRecorder } from '../../hooks/useProctorRecorder';
import { useProctoringFlags } from '../../hooks/useProctoringFlags';
import { useExamGuard } from '../../hooks/useExamGuard';
import ExamGuardOverlay from '../../components/ExamGuardOverlay';
import PdfPreview from '../../components/PdfPreview';
import OralAnswer from '../../components/answers/OralAnswer';
import WhiteboardAnswer from '../../components/answers/WhiteboardAnswer';
import PhotoAnswer from '../../components/answers/PhotoAnswer';
import MathAnswer from '../../components/answers/MathAnswer';
import FinishExamConfirmModal from '../../components/FinishExamConfirmModal';
import '../../styles/exam-guard.css';
const authHeader = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` });

/* --------------------------------- Helpers --------------------------------- */
function asArray(x){ if (Array.isArray(x)) return x; if (x && typeof x === 'object') return Object.values(x); return []; }
function normalizeMedia(raw){
    const arr = asArray(raw || []);
    return arr.map(m => {
        const url  = m.url ?? m.s3_presigned_url ?? m.file_url ?? null;
        const mime = m.mime ?? m.content_type ?? m.mime_type ?? '';
        const fromUrl = m.file_url ? m.file_url.split('/').pop() : null;
        const name = (m.name ?? m.filename ?? m.key ?? fromUrl) ?? 'Załącznik';
        return url ? { url, mime, name } : null;
    }).filter(Boolean);
}
function toNum(x){ const n = Number(x); return Number.isFinite(n) ? n : undefined; }
function normalizeOptions(raw){
    const arr = asArray(raw || []);
    return arr.map(o => {
        const media = normalizeMedia(o.media || o.files || o.attachments);
        const _oid = o.oid ?? o.option_id ?? o.id ?? o.value;
        const oid  = toNum(_oid) ?? String(_oid ?? '');
        const text = o.text ?? o.content ?? o.label ?? String(o.text ?? '');
        return { oid, text, media };
    });
}
function normalizePackForUI(p){
    const raw = p?.questions ? (Array.isArray(p.questions) ? p.questions : Object.values(p.questions)) : [];
    const ordered = Array.isArray(raw) ? raw : Object.values(raw);
    const normQ = ordered.map((q, idx) => {
        const qid = q.qid ?? q.question_id ?? q.id ?? String(idx+1);
        const t = String(q.type || '').toLowerCase();
        const type =
            t === 'single'     ? 'single' :
                t === 'multi'      ? 'multi'  :
                    t === 'textarea'   ? 'textarea' :
                        t === 'essay'      ? 'textarea' :
                            t === 'text'       ? 'text' :
                                t === 'numeric'    ? 'numeric' :
                                    t === 'file'       ? 'file' :
                                        t === 'dropdown'   ? 'single' :
                                            t === 'truefalse'  ? 'single' :
                                                t === 'record'     ? 'oral' :
                                                    t === 'oral'       ? 'oral' :
                                                        t === 'whiteboard' ? 'whiteboard' :
                                                            t === 'math'       ? 'math' :
                                                                t === 'photo'      ? 'photo' :
                                                                    'text';

        const text    = q.text ?? q.content ?? q.prompt ?? q.title ?? `Pytanie ${qid}`;
        const options = normalizeOptions(q.options ?? q.choices ?? q.answer_options);
        const media   = normalizeMedia(q.media ?? q.attachments ?? q.files ?? q.question_files);

        let answer = q.answer ?? q.user_answer ?? q.response ?? null;
        if (type === 'single'){
            if (answer && typeof answer === 'object') answer = answer.option_id ?? answer.oid ?? '';
            const nums = options.every(o => typeof o.oid === 'number');
            if (nums && answer !== '' && answer != null) answer = Number(answer);
        } else if (type === 'multi'){
            if (answer && !Array.isArray(answer)){
                answer = answer.option_ids ?? answer.selected_option_ids ?? [];
            }
            const nums = options.every(o => typeof o.oid === 'number');
            if (nums && Array.isArray(answer)) answer = answer.map(Number).filter(Number.isFinite);
        } else if (type === 'file'){
            if (!(answer && typeof answer === 'object')) answer = null;
        } else if (type === 'numeric'){
            if (answer != null && answer !== '') answer = Number(answer); else answer = '';
        } else {
            if (typeof answer !== 'string') answer = (answer == null ? '' : String(answer));
        }
        return { qid, type, text, options, media, answer };
    });

    return {
        attempt_id:   p.attempt_id ?? p.attemptId ?? p.id,
        exam_title:   p.exam_title ?? p.title ?? 'Egzamin',
        ends_at:      p.ends_at ?? p.end_at ?? null,
        proctoring:   !!(p.proctoring ?? p.proctoring_enabled),
        questions:    normQ,
        building:     !!(p.pending || p.building)
    };
}

function buildFinishPayload(pack, answersState){
    const selected = [], typed = [];
    if (!pack?.questions) return { selected, typed };
    for (const q of pack.questions){
        const a = answersState[q.qid]; if (!a) continue;
        const qidNum = Number(q.qid || q.question_id || q.id); if (!qidNum) continue;
        if (a.type === 'single'){
            const v = a.value; const oid = (v === '' || v == null) ? null : (typeof v === 'number' ? v : Number(v));
            selected.push({ question_id: qidNum, option_ids: oid ? [oid] : [] });
        } else if (a.type === 'multi'){
            const arr = Array.isArray(a.value) ? a.value.map(Number).filter(Boolean) : [];
            selected.push({ question_id: qidNum, option_ids: arr });
        } else if (a.type === 'file'){
            // pliki wiążesz osobno
        } else if (a.type === 'numeric'){
            const num = a.value === '' ? null : Number(a.value);
            typed.push({ question_id: qidNum, answer_text: null, answer_numeric: num });
        } else {
            typed.push({ question_id: qidNum, answer_text: String(a.value ?? ''), answer_numeric: null });
        }
    }
    return { selected, typed };
}

function makeServerBulk(batch, pack){
    const selected = [], typed = [];
    if (!batch?.length || !pack?.questions) return { selected, typed };
    const qidToNum = new Map();
    for (const q of pack.questions){ const n = Number(q.qid || q.question_id || q.id); if (n) qidToNum.set(String(q.qid), n); }
    for (const it of batch){
        const qidNum = qidToNum.get(String(it.qid)); if (!qidNum) continue;
        if (it.type === 'single'){
            const v = it.value; const oid = (v === '' || v == null) ? null : (typeof v === 'number' ? v : Number(v));
            selected.push({ question_id: qidNum, option_ids: oid ? [oid] : [] });
        } else if (it.type === 'multi'){
            const arr = Array.isArray(it.value) ? it.value.map(Number).filter(Boolean) : [];
            selected.push({ question_id: qidNum, option_ids: arr });
        } else if (it.type === 'file'){
            // pomijamy
        } else if (it.type === 'numeric'){
            const num = it.value === '' ? null : Number(it.value);
            typed.push({ question_id: qidNum, answer_text: null, answer_numeric: num });
        } else {
            typed.push({ question_id: qidNum, answer_text: String(it.value ?? ''), answer_numeric: null });
        }
    }
    return { selected, typed };
}

function useDebouncedCallback(cb, delay){
    const ref = useRef({ cb, t:null }); ref.current.cb = cb;
    return useCallback((...args)=>{ if (ref.current.t) clearTimeout(ref.current.t); ref.current.t = setTimeout(()=>ref.current.cb(...args), delay); }, [delay]);
}

/* ----------------------------- Komponent ----------------------------- */
export default function ExamInProgress(){
    const { id } = useParams(); // attemptId
    const nav = useNavigate();
    const guard = useExamGuard({ attemptId: id, enable: true });

    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [pack, setPack] = useState(null);

    const [answers, setAnswers] = useState({});
    const answersRef = useRef(answers);
    useEffect(()=>{ answersRef.current = answers; }, [answers]);

    const [activeQ, setActiveQ] = useState(null);
    const [showFinishConfirm, setShowFinishConfirm] = useState(false);

    const [now, setNow] = useState(Date.now());
    useEffect(()=>{ const t=setInterval(()=>setNow(Date.now()), 1000); return ()=>clearInterval(t); }, []);

    // Notatki
    const [termNote, setTermNote] = useState('');
    const [attemptNote, setAttemptNote] = useState('');

    const endsAt = useMemo(()=> pack?.ends_at ? new Date(pack.ends_at).getTime() : null, [pack?.ends_at]);
    const leftStr = useMemo(()=>{
        if (!endsAt) return '—';
        const s = Math.max(0, Math.ceil((endsAt - Date.now())/1000));
        const hh = Math.floor(s/3600), mm = Math.floor((s%3600)/60), ss = s%60;
        return `${hh>0?hh+':':''}${String(mm).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
    }, [now, endsAt]);

    // Paczka
    useEffect(()=>{
        let mounted = true, pollT = null;
        async function askOnce(){
            try{
                setErr('');
                const raw = await API.pack(id);
                if (!mounted) return;
                if (raw?.pending || raw?.building){ pollT = setTimeout(askOnce, 1200); return; }
                const normalized = normalizePackForUI(raw);
                setPack(normalized);

                const init = {};
                for (const q of normalized.questions){
                    if (q.type === 'multi') init[q.qid] = { type:q.type, value: Array.isArray(q.answer) ? q.answer : [], dirty:false };
                    else if (q.type === 'file'){ const v = q.answer || null; init[q.qid] = { type:q.type, value: v, dirty:false, fileKey: v?.file_key || null, fileMeta: v?.meta || null }; }
                    else if (q.type === 'numeric'){ const num = (q.answer != null && q.answer !== '') ? Number(q.answer) : ''; init[q.qid] = { type:q.type, value:num, dirty:false }; }
                    else init[q.qid] = { type:q.type, value:(q.answer ?? ''), dirty:false };
                }
                setAnswers(init);
                if (normalized.questions.length) setActiveQ(normalized.questions[0].qid);
            }catch(e){
                const msg = String(e?.message || '');
                if (msg.includes('not_verified')) { nav(`/student/egzamin/${id}/weryfikacja`); return; }
                if (msg.includes('not_yet')) { nav(`/student/egzamin/${id}/oczekiwanie`); return; }
                if (msg.includes('session_active') || msg.includes('no_session') || msg.includes('lock')) { nav(`/student/egzamin/${id}/oczekiwanie`); return; }
                if (msg.includes('pack') && msg.includes('building')) { pollT = setTimeout(askOnce, 1200); return; }
                setErr(msg || 'Nie udało się pobrać egzaminu'); setLoading(false);
            }finally{ if (mounted && !pollT) setLoading(false); }
        }
        askOnce();
        return ()=>{ mounted=false; if (pollT) clearTimeout(pollT); };
    }, [id, nav]);

    // Autosave
    const pendingRef = useRef([]);
    const enqueueSave = useDebouncedCallback((qid, payload)=>{
        pendingRef.current = [ ...pendingRef.current.filter(x => x.qid !== qid), { qid, value: payload.value, type: payload.type } ];
    }, 800);

    useEffect(()=>{ const t = setInterval(async ()=>{
        if (!pendingRef.current.length || !pack) return;
        const batch = pendingRef.current; pendingRef.current = [];
        try{
            const serverBulk = makeServerBulk(batch, pack);
            if (serverBulk.selected.length || serverBulk.typed.length){ await API.answersBulk(id, serverBulk);  refreshTimeLight(); }
            setAnswers(prev=>{ const next = { ...prev }; for (const a of batch){ if (next[a.qid]) next[a.qid] = { ...next[a.qid], dirty:false }; } return next; });
        }catch(e){ pendingRef.current = [...batch, ...pendingRef.current]; }
    }, 30000); return ()=>clearInterval(t); }, [id, pack?.attempt_id, pack?.questions]);

    async function flushNow(){
        if (!pendingRef.current.length || !pack) return;
        const batch = pendingRef.current; pendingRef.current = [];
        try{
            const serverBulk = makeServerBulk(batch, pack);
            if (serverBulk.selected.length || serverBulk.typed.length){ await API.answersBulk(id, serverBulk); }
            setAnswers(prev=>{ const next = { ...prev }; for (const a of batch){ if (next[a.qid]) next[a.qid] = { ...next[a.qid], dirty:false }; } return next; });
        }catch(e){ pendingRef.current = [...batch, ...pendingRef.current]; setErr(e.message || 'Błąd zapisu odpowiedzi'); }
    }

    // ————— LEKKI REFRESH czasu + notatek —————
    async function refreshTimeLight() {
        try {
            const r = await fetch(`/api/exam/attempts/${id}/time`, {
                credentials: 'include',
                headers: { ...authHeader() }
            });
            if (!r.ok) return;
            const j = await r.json();
            if (j?.ends_at) setPack(prev => (prev && prev.ends_at !== j.ends_at ? { ...prev, ends_at: j.ends_at } : prev));
            setTermNote(j?.term_note || '');
            setAttemptNote(j?.attempt_note || '');
        } catch {
            // cisza – best effort
        }
    }

    // Minutowy polling czasu + notatek
    useEffect(() => {
        if (!pack?.attempt_id) return;
        refreshTimeLight(); // pierwszy strzał
        const poll = setInterval(() => { refreshTimeLight(); }, 60000);
        return () => clearInterval(poll);
    }, [id, pack?.attempt_id]);

    function setAnswer(q, value){
        setAnswers(prev=>{
            const cur = prev[q.qid] || { type:q.type, value:null, dirty:false };
            const next = { ...cur, value, dirty:true };
            return { ...prev, [q.qid]: next };
        });
        enqueueSave(q.qid, { qid: q.qid, value, type: q.type });
    }

    async function handleFileChange(q, file){
        if (!file) return;
        try{
            const init = await API.ansFileInit(id, q.qid, {
                filename: file.name, contentType: file.type || 'application/octet-stream', sizeBytes: file.size
            });
            const put = await fetch(init.url, { method: init.method || 'PUT', headers: init.headers || {}, body: file, credentials:'include' });
            if (!put.ok){ const t = await put.text().catch(()=>'(brak treści)'); throw new Error(`Upload nieudany: ${put.status} ${t}`); }
            await API.ansFileFin(id, q.qid, { key: init.key, meta: { name:file.name, type:file.type, size:file.size } });

            const fileValue = { file_key: init.key, meta: { name:file.name, type:file.type, size:file.size } };
            setAnswers(prev => ({ ...prev, [q.qid]: { type:'file', value:fileValue, fileKey:init.key, fileMeta:fileValue.meta, dirty:false } }));
        }catch(e){ setErr(e.message || 'Błąd uploadu pliku'); }
    }

    useEffect(()=>{
        if (!pack) return;
        async function sendKeepalive(){
            try{
                const snapshot = Object.entries(answersRef.current).map(([qid, v]) => ({ qid, type: v.type, value: v.value }));
                const bulkLocal = makeServerBulk(snapshot, pack);
                if (!bulkLocal.selected.length && !bulkLocal.typed.length) return;
                await fetch(`/api/exam/attempts/${id}/answers/bulk`, {
                    method: 'POST',
                    headers: { 'Content-Type':'application/json', ...authHeader() },
                    credentials: 'include',
                    keepalive: true,
                    body: JSON.stringify(bulkLocal)
                });
            }catch{}
        }
        function onVisibility(){ if (document.visibilityState === 'hidden') sendKeepalive(); }
        function onBeforeUnload(){ sendKeepalive(); }
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('beforeunload', onBeforeUnload);
        return ()=>{ document.removeEventListener('visibilitychange', onVisibility); window.removeEventListener('beforeunload', onBeforeUnload); sendKeepalive(); };
    }, [id, pack?.attempt_id, pack?.questions]);

    async function finishExam(){
        try{
            await flushNow();
            const payload = buildFinishPayload(pack, answersRef.current);
            await API.finish(id, payload);
            nav(`/student/egzamin/${id}/koniec`);
        }catch(e){ setErr(e.message || 'Nie udało się zakończyć egzaminu'); }
    }

    function confirmFinishExam(){
        setShowFinishConfirm(true);
    }

    function handleConfirmedFinishExam(){
        setShowFinishConfirm(false);
        finishExam();
    }

    useEffect(()=>{ if (!endsAt) return; const t = setInterval(()=>{ if (Date.now() >= endsAt){ clearInterval(t); finishExam().catch(()=>{}); } }, 1000); return ()=>clearInterval(t); }, [endsAt]); // eslint-disable-line

    /* --------------------------- PROCTORING: nagrywanie + flagi --------------------------- */
    const [recCfg, setRecCfg] = useState({ chunkSeconds: 30, mime: 'video/webm;codecs=vp8,opus' });

    const recorder = useProctorRecorder({
        attemptId: id,
        chunkSeconds: recCfg.chunkSeconds,
        onError: (e) => console.warn('Recorder error', e),
    });

    const startedRef = useRef(false);
    const finalizeOnceRef = useRef(false);
    async function finalizeRecordingOnce(attemptId){
        if (finalizeOnceRef.current) return;
        finalizeOnceRef.current = true;
        try {
            await fetch(`/api/rec/attempts/${attemptId}/finalize`, { method:'POST', credentials:'include', headers: authHeader() });
        } catch {}
    }

    const [ensureDone, setEnsureDone] = useState(false);

    useEffect(() => {
        if (!pack?.proctoring) return;
        let cancelled = false;
        (async () => {
            try {
                const ensureResp = await fetch(`/api/rec/attempts/${id}/ensure`, {
                    method:'POST',
                    headers:{ 'Content-Type':'application/json', ...authHeader() },
                    credentials:'include',
                    body: JSON.stringify({ clientStartMs: Date.now(), mime: 'video/webm;codecs=vp8,opus' })
                });
                const ensure = await ensureResp.json();
                if (!ensure?.ok) throw new Error(ensure?.blad || 'rec/ensure fail');
                if (!cancelled) { setRecCfg({ chunkSeconds: ensure.chunkSeconds, mime: ensure.mime }); setEnsureDone(true); }
            } catch (e) {
                console.error('[proctor] ensure fail', e);
                setErr('Nie udało się uruchomić nagrywania z kamery (proctoring). Materiał wideo/flag może być niekompletny.');
            }
        })();
        return () => { cancelled = true; setEnsureDone(false); };
    }, [id, pack?.proctoring]);

    useEffect(() => {
        if (!pack?.proctoring) return;
        (async () => {
            if (ensureDone && !startedRef.current) {
                try { await recorder.start(); startedRef.current = true; }
                catch (e) { console.error('[proctor] start fail', e); setErr('Nie udało się uruchomić nagrywania (proctoring).'); }
            }
        })();
        return () => { try { recorder.stop(); } catch {} startedRef.current = false; finalizeRecordingOnce(id); };
    }, [id, pack?.proctoring, ensureDone]); // bez "recorder" w deps

    useEffect(() => {
        const onUnload = () => { finalizeRecordingOnce(id); };
        window.addEventListener('beforeunload', onUnload);
        return () => window.removeEventListener('beforeunload', onUnload);
    }, [id]);

    // Flagi – niezależnie od nagrywania
    useProctoringFlags({
        attemptId: id,
        enabled: !!pack?.proctoring,
        faceIntervalMs: 1400,
    });

    /* --------------------------------- Render --------------------------------- */
    if (loading) return <div style={{ maxWidth: 1100, margin:'24px auto' }}>Ładowanie…</div>;
    if (!pack) return <div style={{ maxWidth: 1100, margin:'24px auto', color:'#a00' }}>{err || 'Brak danych egzaminu'}</div>;

    const qs = pack.questions || [];
    const current = qs.find(x => x.qid === activeQ) || qs[0];

    async function gotoPrev(){
        guard.markUiAction();
        const idx = qs.findIndex(x=>x.qid===current.qid);
        if (idx>0){ await flushNow().catch(()=>{}); setActiveQ(qs[idx-1].qid); }
    }
    async function gotoNext(){
        guard.markUiAction();
        const idx = qs.findIndex(x=>x.qid===current.qid);
        if (idx < qs.length-1){ await flushNow().catch(()=>{}); setActiveQ(qs[idx+1].qid); }
    }

    return (
        <div className="exam-guard" style={{ maxWidth: 1200, padding:'0 16px', display:'grid', gridTemplateColumns:'260px 1fr', gap:16, marginTop:100 }}>
            <aside style={{ border:'1px solid #eee', borderRadius:12, padding:12, backgroundColor:'white' }}>
                <div data-tour="timer"  style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                    <strong>{pack.exam_title || 'Egzamin'}</strong>
                    <span style={{ padding:'2px 8px', borderRadius:8, background:'#f6f6ff' }}>Pozostało: {leftStr}</span>
                </div>
                <div data-tour="grid" style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8 }}>
                    {qs.map((q, idx)=>{
                        const a = answers[q.qid];
                        const answered = a?.type === 'multi'
                            ? (Array.isArray(a?.value) && a.value.length>0)
                            : (a?.type === 'file' ? !!a?.fileKey || !!a?.value : (a?.value != null && String(a.value).trim() !== ''));
                        return (
                            <button
                                key={q.qid}
                                onClick={async()=>{ guard.markUiAction();  await flushNow().catch(()=>{}); setActiveQ(q.qid); }}
                                style={{
                                    borderRadius:10, padding:'8px 0',
                                    border: q.qid===activeQ ? '2px solid #4a6' : '1px solid #ddd',
                                    background: answered ? '#e7f6ec' : '#fff',
                                    fontWeight: q.qid===activeQ ? 700 : 500
                                }}
                                title={`Pytanie ${idx+1}`}
                            >
                                {idx+1}
                            </button>
                        );
                    })}
                </div>

                <div data-tour="finish" style={{ marginTop:12, display:'grid', gap:8 }}>
                    <button onClick={confirmFinishExam} style={{ background:'#c00', color:'#fff', border:'none', padding:'10px 12px', borderRadius:8 }}>
                        Zakończ egzamin
                    </button>
                    <small style={{ opacity:0.8 }}>Zielone okienko oznacza zapisaną odpowiedź. Na te pytania zostały udzielone odpowiedzi.</small>
                    {err && <div style={{ color:'#a00' }}>{err}</div>}
                </div>
            </aside>

            <main style={{ border:'1px solid #eee', borderRadius:12, padding:16, backgroundColor:'white' }}>
                {!current ? (
                    <div>Brak pytań.</div>
                ) : (
                    <QuestionCard
                        q={current}
                        value={answers[current.qid]}
                        onChange={(val)=>setAnswer(current, val)}
                        onFile={file => handleFileChange(current, file)}
                        attemptId={id}
                    />
                )}
                <div data-tour="next" style={{ display:'flex', justifyContent:'space-between', marginTop:16 }}>
                    <button  className={'but-smal'} onClick={gotoPrev} disabled={!current || qs.findIndex(x=>x.qid===current.qid) <= 0}>← Poprzednie</button>
                    <button className={'but-smal'} onClick={gotoNext} disabled={!current || qs.findIndex(x=>x.qid===current.qid) >= qs.length-1}>Następne →</button>
                </div>

                {(termNote || attemptNote) && (
                    <div style={{ marginTop:30, border:'1px solid #e5e7eb', background:'#f9fafb', borderRadius:10, padding:12, display:'grid', gap:8 }}>
                        {termNote && (
                            <div>
                                <div style={{ fontWeight:700, fontSize:13, color:'#374151' }}>Komunikat prowadzącego </div>
                                <div style={{ whiteSpace:'pre-wrap' }}>{termNote}</div>
                            </div>
                        )}
                        {attemptNote && (
                            <div>
                                <div style={{ fontWeight:700, fontSize:13, color:'#374151' }}>Komunikat indywidualny</div>
                                <div style={{ whiteSpace:'pre-wrap' }}>{attemptNote}</div>
                            </div>
                        )}
                    </div>
                )}
            </main>
            <FinishExamConfirmModal
                open={showFinishConfirm}
                onCancel={() => setShowFinishConfirm(false)}
                onConfirm={handleConfirmedFinishExam}
            />
            <ExamGuardOverlay visible={guard.needsFullscreen} onReenter={guard.enterFullscreen} />
        </div>
    );
}

/* ---------------------------- Karta pytania ---------------------------- */
function QuestionCard({ q, value, onChange, onFile, attemptId }){
    const aVal = value?.value;
    const media = q.media || [];
    const options = q.options || [];

    return (
        <div style={{ display:'grid', gap:12 }}>
            <div style={{ fontSize:18, fontWeight:700 }}>{q.text}</div>

            {media.length>0 && (
                <div style={{ display:'grid', gap:8 }}>
                    {media.map((m, i)=>{
                        const mime = String(m.mime || '').toLowerCase();
                        if (mime === 'application/pdf' || mime.startsWith('application/pdf')) {
                            return <PdfPreview key={i} url={m.url} name={m.name || 'Dokument PDF'} />;
                        }
                        if (m.mime && m.mime.startsWith('image/'))  return <img   key={i} src={m.url} alt={m.name || 'obraz'} style={{ maxWidth:'100%', borderRadius:8 }} />;
                        if (m.mime && m.mime.startsWith('video/'))  return <video key={i} src={m.url} controls style={{ width:'100%', borderRadius:8 }} />;
                        if (m.mime && m.mime.startsWith('audio/'))  return <audio key={i} src={m.url} controls />;
                        return <a key={i} href={m.url} target="_blank" rel="noreferrer">{m.name || 'Załącznik'}</a>;
                    })}
                </div>
            )}

            {q.type === 'single' && (
                <div style={{ display:'grid', gap:12 }}>
                    {options.map(opt => (
                        <div key={opt.oid} style={{ display:'grid', gap:6 }}>
                            <label style={{ display:'flex', gap:8, alignItems:'center' }}>
                                <input type="radio" name={`q-${q.qid}`} checked={aVal === opt.oid} onChange={()=>onChange(opt.oid)} />
                                <span>{opt.text}</span>
                            </label>
                            {!!opt.media?.length && (
                                <div style={{ display:'grid', gap:6, marginLeft:28 }}>
                                    {opt.media.map((m, i)=>{
                                        const mime = String(m.mime || '').toLowerCase();
                                        if (mime === 'application/pdf' || mime.startsWith('application/pdf')) {
                                            return <PdfPreview key={i} url={m.url} name={m.name || 'Dokument PDF'} />;
                                        }
                                        if (m.mime && m.mime.startsWith('image/')) return <img key={i} src={m.url} alt={m.name || 'obraz opcji'} style={{ maxWidth:'100%', borderRadius:6 }} />;
                                        if (m.mime && m.mime.startsWith('video/')) return <video key={i} src={m.url} controls style={{ width:'100%', borderRadius:6 }} />;
                                        if (m.mime && m.mime.startsWith('audio/')) return <audio key={i} src={m.url} controls />;
                                        return <a key={i} href={m.url} target="_blank" rel="noreferrer">{m.name || 'Załącznik'}</a>;
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {q.type === 'multi' && (
                <div style={{ display:'grid', gap:12 }}>
                    {options.map(opt => {
                        const arr = Array.isArray(aVal) ? aVal : [];
                        const checked = arr.includes(opt.oid);
                        return (
                            <div key={opt.oid} style={{ display:'grid', gap:6 }}>
                                <label style={{ display:'flex', gap:8, alignItems:'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e)=>{
                                            const next = new Set(arr);
                                            if (e.target.checked) next.add(opt.oid); else next.delete(opt.oid);
                                            onChange(Array.from(next));
                                        }}
                                    />
                                    <span>{opt.text}</span>
                                </label>
                                {!!opt.media?.length && (
                                    <div style={{ display:'grid', gap:6, marginLeft:28 }}>
                                        {opt.media.map((m, i)=>{
                                            const mime = String(m.mime || '').toLowerCase();
                                            if (mime === 'application/pdf' || mime.startsWith('application/pdf')) {
                                                return <PdfPreview key={i} url={m.url} name={m.name || 'Dokument PDF'} />;
                                            }
                                            if (m.mime && m.mime.startsWith('image/')) return <img key={i} src={m.url} alt={m.name || 'obraz opcji'} style={{ maxWidth:'100%', borderRadius:6 }} />;
                                            if (m.mime && m.mime.startsWith('video/')) return <video key={i} src={m.url} controls style={{ width:'100%', borderRadius:6 }} />;
                                            if (m.mime && m.mime.startsWith('audio/')) return <audio key={i} src={m.url} controls />;
                                            return <a key={i} href={m.url} target="_blank" rel="noreferrer">{m.name || 'Załącznik'}</a>;
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {q.type === 'text' && (
                <input type="text" value={typeof aVal === 'string' ? aVal : ''} onChange={e=>onChange(e.target.value)} placeholder="Twoja odpowiedź" />
            )}

            {q.type === 'numeric' && (
                <input type="number" value={aVal ?? ''} onChange={e=>onChange(e.target.value === '' ? '' : Number(e.target.value))} placeholder="Wpisz liczbę" />
            )}

            {q.type === 'textarea' && (
                <textarea value={typeof aVal === 'string' ? aVal : ''} onChange={e=>onChange(e.target.value)} rows={6} placeholder="Twoja odpowiedź" />
            )}

            {q.type === 'file' && (
                <div style={{ display:'grid', gap:8 }}>
                    <input type="file" onChange={e=>onFile(e.target.files?.[0] || null)} />
                    {value?.fileKey && (
                        <div style={{ fontSize:14, opacity:0.9 }}>
                            Przesłano plik: <code>{value.fileMeta?.name || value.fileKey}</code>
                        </div>
                    )}
                </div>
            )}
            {q.type === 'oral' && (
                <OralAnswer
                    attemptId={String(attemptId || '') || undefined}
                    questionId={q.qid}
                    onSaved={({ file_key, meta }) => onChange({ file_key, meta })}
                />
            )}

            {q.type === 'whiteboard' && (
                <WhiteboardAnswer
                    attemptId={String(attemptId || '') || undefined}
                    questionId={q.qid}
                    onSaved={({ file_key, meta }) => onChange({ file_key, meta })}
                />
            )}

            {q.type === 'photo' && (
                <PhotoAnswer
                    attemptId={String(attemptId || '') || undefined}
                    questionId={q.qid}
                    onSaved={({ file_key, meta }) => onChange({ file_key, meta })}
                />
            )}

            {q.type === 'math' && (
                <MathAnswer
                    attemptId={String(attemptId || '') || undefined}
                    questionId={q.qid}
                    onSaved={({ file_key, meta }) => onChange({ file_key, meta })}
                />
            )}
        </div>
    );
}
